import React from 'react';
import { Card, Image, Modal, Table } from 'antd';
import type { TableProps } from 'antd';
import emiter, { _EVENTS } from '../../../../events';
import { useOrderList } from '../../../../hooks/order-management/order-list';
import EnumKeyToText from './enumkeyTotext';
import { jsonParseData } from '../../../../utils/index';
import SPButton from '../../../carRental/base/SPButton';

interface RefundTypePropsType {
	data?: any;
	enums?: any;
	permission?: any;
}

const RefundType: React.FC<RefundTypePropsType> = props => {
	const { data, enums, permission } = props;

	return <EnumKeyToText data={data} enums={enums} valueKey={'refund_type'} nameSpace={'deduct_refund_type'} />;
};

interface RefundStatusPropsType {
	data?: any;
	enums?: any;
}

const RefundStatus: React.FC<RefundStatusPropsType> = props => {
	const { data, enums } = props;

	return <EnumKeyToText data={data} enums={enums} valueKey={'refund_status'} nameSpace={'deduct_refund_status'} />;
};

interface DeductTypePropsType {
	data?: any;
	enums?: any;
}

const DeductType: React.FC<DeductTypePropsType> = props => {
	const { data, enums } = props;

	return <EnumKeyToText data={data} enums={enums} valueKey={'deduct_type'} nameSpace={'deposit_deduct_type'} />;
};

interface DeductStatusPropsType {
	data?: any;
	enums?: any;
}

const DeductStatus: React.FC<DeductStatusPropsType> = props => {
	const { data, enums } = props;

	return (
		<div>
			<EnumKeyToText data={data} enums={enums} valueKey={'deduct_status'} nameSpace={'deduct_status'} />
		</div>
	);
};

interface EvidencePropsType {
	data?: any;
}

const Evidence: React.FC<EvidencePropsType> = props => {
	const { data } = props;
	const arr = jsonParseData(data, []);
	if (Array.isArray(arr) && arr.length) {
		return (
			<div style={{ display: 'flex' }}>
				{arr.map((item: any) => (
					<div style={{ marginRight: '10px' }}>
						<Image width={60} height={60} src={item} />
					</div>
				))}
			</div>
		);
	}
	return null;
};

interface TableActionBarPropsType {
	data?: any;
	orderCode?: any;
	allData?: any;
	permission?: any;
}

const TableActionBar: React.FC<TableActionBarPropsType> = props => {
	const { data, orderCode, allData, permission } = props;
	const { deductRevertSubmit } = useOrderList();

	const operations = data?.operations;

	const existBtn = Array.isArray(operations) && operations.length;

	const confimWithdraw = () => {
		deductRevertSubmit({
			order_code: orderCode,
			deduct_no: data.deduct_no,
		});
	};

	const withdraw = () => {
		Modal.confirm({
			centered: true,
			title: '确认撤销扣款失败的押金订单吗？确认后将撤销押金定时追缴，再次申请押金扣款时记得累加本次扣款金额',
			okText: '确认',
        	cancelText: '取消',
			onOk: confimWithdraw,
		});
	};

	const refund = () => {
		emiter.emit(_EVENTS.ORDER_MANAGEMENT.ORDER_LIST.DEPOSIT_REFUND, { ...allData, ...data, order_code: orderCode });
	};

	return existBtn ? (
		<>
			{operations.includes('revert') ? (
				<SPButton
					permissionKey="deduct-revert"
					permission={permission}
					type="link"
					onClick={withdraw}
					disabled={false}
				>
					撤回扣款
				</SPButton>
			) : null}
			{operations.includes('refund') ? (
				<SPButton
					permissionKey="deduct-refund"
					permission={permission}
					type="link"
					onClick={refund}
					disabled={false}
				>
					退款
				</SPButton>
			) : null}
		</>
	) : null;
};

interface PropsType {
	enums?: any;
	data?: any;
	orderCode?: any;
	allData?: any;
	changePriceFormat?: any;
	permission?: any;
}

interface TableDataType {
	id: number;
	username?: string;
	cell_encrypt?: string;
	user_status?: number;
	role_list?: any;
	store_list?: any;
	create_time?: string;
	update_time?: string;
}

interface RefundTableDataType {
	id: number;
	username?: string;
	cell_encrypt?: string;
	user_status?: number;
	role_list?: any;
	store_list?: any;
	create_time?: string;
	update_time?: string;
}

const DeductionDetails: React.FC<PropsType> = props => {
	const { data, orderCode, enums, allData, changePriceFormat, permission } = props;
	const columns: TableProps<TableDataType>['columns'] = [
		{ title: '扣款日期', dataIndex: 'deduct_time', key: 'deduct_time' },
		{
			title: '扣款类型',
			dataIndex: 'deduct_type',
			key: 'deduct_type',
			render: (_, record) => <DeductType data={record} enums={enums} />,
		},
		{
			title: '扣款备注',
			dataIndex: 'reason',
			key: 'reason',
		},
		{
			title: '扣款金额',
			dataIndex: 'amount',
			key: 'amount',
			render: text => changePriceFormat(text),
		},
		{
			title: '扣款状态',
			dataIndex: 'deduct_status',
			key: 'deduct_status',
			render: (_, record) => <DeductStatus data={record} enums={enums} />,
		},
		{
			title: '退款金额',
			dataIndex: 'refund_amount',
			key: 'refund_amount',
			render: text => changePriceFormat(text),
		},
		{
			title: '剩余扣款',
			dataIndex: 'remain_amount',
			key: 'remain_amount',
			render: text => changePriceFormat(text),
		},
		{
			title: '扣款凭证',
			dataIndex: 'evidence',
			key: 'evidence',
			render: text => <Evidence data={text} />,
		},
		{
			title: '操作人',
			dataIndex: 'operator',
			key: 'operator',
		},
		{
			title: '操作',
			dataIndex: 'action',
			key: 'action',
			align: 'right',
			render: (_, record) => (
				<TableActionBar permission={permission} data={record} orderCode={orderCode} allData={allData} />
			),
		},
	];

	const refundColumns: TableProps<RefundTableDataType>['columns'] = [
		{ title: '退款日期', dataIndex: 'refund_time', key: 'refund_time' },
		{
			title: '退款类型',
			dataIndex: 'refund_type',
			key: 'refund_type',
			render: (_, record) => <RefundType data={record} enums={enums} />,
		},
		{
			title: '退款备注',
			dataIndex: 'remark',
			key: 'remark',
		},
		{
			title: '退款状态',
			dataIndex: 'refund_status',
			key: 'refund_status',
			render: (_, record) => <RefundStatus data={record} enums={enums} />,
		},
		{
			title: '退款金额',
			dataIndex: 'amount',
			key: 'amount',
			render: text => changePriceFormat(text),
		},
		{
			title: '退款凭证',
			dataIndex: 'evidence',
			key: 'evidence',
			render: text => <Evidence data={text} />,
		},
		{
			title: '操作人',
			dataIndex: 'operator',
			key: 'operator',
		},
	];

	return (
		<Card title="扣款明细" style={{ marginTop: '20px' }}>
			<Table
				title={() => '押金扣款：'}
				columns={columns}
				dataSource={data?.deduct?.deduct_items}
				rowKey={cols => cols.id}
				pagination={false}
			/>
			<Table
				title={() => '扣款后退款：'}
				columns={refundColumns}
				dataSource={data?.refund?.refund_items}
				rowKey={cols => cols.id}
				pagination={false}
			/>
		</Card>
	);
};

export default DeductionDetails;
